import { App, Spin } from 'antd';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppConfig, container } from 'src/inversify.config';
import { formatMessage } from 'src/core/services/http.service';
import { ListState, Query } from 'src/core/stores/data-store';
import { clone } from 'src/core/utils/object';
import { RoleAssignmentSummary, RoleAssignmentSummaryStore } from 'src/stores/role-assignments-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';

import AddGroupRoleModal from './group-role-assignment-modal';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { LicenseCreationAttributesStore } from 'src/stores/licensing-store';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { IdentityType, PermissionsConstants } from 'src/common/types';
import { useIdentity } from 'src/core/services/authentication';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import moment from 'moment';
import { licenseManagerRoleName, licenseReviewerRoleName, StructmasterUserRoleName } from 'src/stores/roles-store';
import UpdateRoleAttributesDialog from 'src/pages/roles/role-attributes/update-role-attributes';
import i18next from 'i18next';
import { GroupType } from 'src/stores/groups-store';
import { useCheckGlobalLicensesPermission } from 'src/common/hooks/use-check-global-licenses-permission';

const permissions = [PermissionsConstants.ManageLicenses];
const licenseRoles = [licenseManagerRoleName, licenseReviewerRoleName];

const GroupLicenseRolesTab: FC<{
  groupId: string;
  groupOrganizationId?: string;
  isPartnerOrg: boolean;
  groupName: string;
  organizationName: string;
}> = ({ groupId, groupOrganizationId, isPartnerOrg, groupName, organizationName }) => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const groupRolesStore = container.get(RoleAssignmentSummaryStore);
  const groupRolesState = groupRolesStore.state;
  const [addRolesVisible, setAddRolesVisible] = useState(false);

  const identity = useIdentity();
  const [scopeFilter] = useState('');
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const [userPermissionsSet, checkingTenantPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const [hasGlobalLicensemanagerPermission, checkingGlobalPermissions] = useCheckGlobalLicensesPermission();

  const checkingPermissions = checkingTenantPermissions || checkingGlobalPermissions;

  const hasLicenseManagerPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageLicenses);
  const [licenseAttrVisible, setLicenseAttrVisible] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<RoleAssignmentSummary>();
  const [selectedRowsLength, setSelectedRowsLength] = useState<number>(0);
  const { modal } = App.useApp();

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'Role', direction: 'Ascending', useProfile: true }],
    skip: 0,
    take: 10,
    filter: [`identityId eq ${groupId}`],
  } as Query);

  useEffect(() => {
    const newQuery = {
      searchQuery: '',
      orderBy: [{ field: 'Role', direction: 'Ascending', useProfile: true }],
      skip: 0,
      take: 10,
      filter: [`identityId eq ${groupId}`],
    } as Query;
    setQuery(newQuery);
    groupRolesStore.load(newQuery);
  }, [groupId, groupRolesStore]);

  const onRefresh = () => {
    setSelectedAssignment(undefined);
    groupRolesStore.load(query);
  };

  const expirationDateFilter = (query.filter as any)['expirationDate'];
  const hasExpirationDateFilter =
    expirationDateFilter !== null &&
    expirationDateFilter !== undefined &&
    ((expirationDateFilter.ge !== null && expirationDateFilter.ge !== undefined) ||
      (expirationDateFilter.le !== null && expirationDateFilter.le !== undefined));
  const expirationDateHeader = t('ExpirationDate') + ' ' + (hasExpirationDateFilter ? '[*]' : '');

  const tableModel = {
    query,
    rowDisabled: (data) =>
      (data.role === licenseManagerRoleName && !hasGlobalLicensemanagerPermission) ||
      (data.role === StructmasterUserRoleName && !hasLicenseManagerPermission),
    columns: [
      {
        field: 'Role',
        sortable: true,
        sortField: 'Role',
        useProfile: true,
        searchable: true,
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter baseUrl={groupRolesStore.baseUrl} fieldName={'Role/Name'} fieldValue={'role'} filter={`identityId eq ${groupId}`} />
        ),
        fiterField: 'Role/Name',
        title: t('Role'),
        renderer: (data) => (
          <span
            className={
              (data.role === licenseManagerRoleName && !hasGlobalLicensemanagerPermission) || data.role === StructmasterUserRoleName
                ? 'hide-checkbox'
                : ''
            }
          >
            {data.role}
          </span>
        ),
      },
      {
        field: 'ScopeTitle',
        sortable: true,
        sortField: 'ScopeTitle',
        useProfile: true,
        searchable: true,
        applyPartialFilter: true,
        fiterField: 'Scope/Title',
        title: t('Scope'),
        renderer: (data) => <span>{data.scopeTitle}</span>,
      },
      {
        field: 'RoleExpirationDate',
        sortable: true,
        searchable: false,
        title: t(expirationDateHeader),
        filter: <DateRangeFilter allowClear />,
        renderer: (data) => <span>{data.expirationDate ? moment(data.expirationDate).locale(i18next.language).format(getDateFormat()) : ''}</span>,
      },
      {
        field: 'UpdateRoleAttributes',
        sortable: false,
        searchable: false,
        title: '',
        renderer: (data) =>
          (data.role === licenseManagerRoleName && !hasGlobalLicensemanagerPermission) ||
          (data.role === StructmasterUserRoleName && !hasLicenseManagerPermission) ? (
            ''
          ) : (
            <span
              className="clickable-span strong"
              onClick={() => {
                setSelectedAssignment(data);
                setLicenseAttrVisible(true);
              }}
            >
              {t('UpdateRoleAttributes')}
            </span>
          ),
      },
      {
        field: 'Delete',
        sortable: false,
        searchable: false,
        title: '',
        renderer: (data) =>
          (data.role === licenseManagerRoleName && !hasGlobalLicensemanagerPermission) ||
          (data.role === StructmasterUserRoleName && !hasLicenseManagerPermission) ? (
            ''
          ) : (
            <span
              className="clickable-span strong"
              onClick={() => {
                deleteItem(data);
              }}
            >
              {t('Delete')}
            </span>
          ),
      },
    ],
    data: groupRolesState.value as ListState<RoleAssignmentSummary>,
    sortFields: [],
  } as TableModel<RoleAssignmentSummary>;

  const getFilterArray = (filterData: any) => {
    if (filterData == null) return [] as any[];
    if (filterData instanceof Array || Array.isArray(filterData)) return filterData as any[];

    let rawData: string[] = [];
    for (const [key, value] of Object.entries(filterData)) {
      let newItem: string;

      if (key === 'RoleExpirationDate') {
        if (value) {
          newItem = `${key} ge ${(value as any).ge.value} and ${key} le ${(value as any).le.value}`;
        } else {
          newItem = 'RoleExpirationDate';
        }
      } else {
        newItem = isNaN(key as any) ? `${key} eq '${value}'` : `${value}`;
      }

      rawData = rawData.filter((x) => x.split(' ')[0] !== newItem.split(' ')[0]);
      if (value) rawData.push(newItem);
    }

    return rawData;
  };

  const includeSearches = (q: Query) => {
    const nextQuery = clone<Query>(q);

    const nextQueryfilter = getFilterArray(nextQuery.filter).filter((x) => x !== scopeFilter);

    let nextFiters = [];
    if (scopeFilter?.length !== 0) nextFiters.push(scopeFilter);

    nextQuery.filter = [...nextQueryfilter, ...nextFiters];
    return nextQuery;
  };

  const onSelectionChanged = (rowsKeys: string[]) => {
    setSelectedRowsLength(rowsKeys.length);
    return rowsKeys;
  };
  const deleteItem = async (item: RoleAssignmentSummary) => {
    if (groupRolesState.items.length === 1) {
      modal.error({ title: t('Error'), content: t('LicenseManagerGroupsMustHaveAtLeastOneRole') });
      return;
    }
    let ignoreRoleForDelete = item.role === licenseManagerRoleName && !hasGlobalLicensemanagerPermission;
    ignoreRoleForDelete = ignoreRoleForDelete || item.role === StructmasterUserRoleName;
    if (ignoreRoleForDelete) {
      return;
    }
    await groupRolesStore.delete(item.id).then(async () => {
      if (item.role === licenseManagerRoleName) {
        setSelectedAssignment(undefined);
        await licenseCreationAttributesStore.deleteByUserScope({
          identityId: item.identityId,
          scopeId: item.scopeId,
          identityType: IdentityType.Group,
        });
      }
    });
    await groupRolesStore.load(query);
  };

  return (
    <div className="table-small-toolbar">
      <Spin spinning={checkingPermissions || groupRolesState.isBusy.get() || licenseCreationAttributesState.isBusy.get()}>
        <TableView
          selectedRowsNumber={selectedRowsLength ?? 0}
          rowKey="id"
          title={t('RolesAssigned')}
          titlePadding={'0 40px'}
          canCreateNew={
            hasGlobalLicensemanagerPermission &&
            licenseRoles.filter((licenseRole) => !groupRolesState.items.value.some((groupRole) => groupRole.role === licenseRole)).length > 0
          }
          onNewItem={() => setAddRolesVisible(true)}
          canDelete={false}
          editLabel={t('EditSelected')}
          onSelectionChanged={onSelectionChanged}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            const augmentedQuery = includeSearches(query);
            setQuery(augmentedQuery);
            groupRolesStore.load(augmentedQuery);
          }}
          onRefresh={onRefresh}
          model={groupRolesState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            groupRolesState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(groupRolesState.errorMessage.value) : t('GroupRolesTableError'))
          }
        />
        {addRolesVisible && (
          <AddGroupRoleModal
            groupType={GroupType.License}
            availableRoleNames={licenseRoles.filter(
              (licenseRole) => !groupRolesState.items.value.some((groupRole) => groupRole.role === licenseRole)
            )}
            groupName={groupName}
            organizationName={organizationName}
            isPartnerOrg={isPartnerOrg}
            groupId={groupId}
            groupOrganization={groupOrganizationId}
            onClose={(response, addAnother) => {
              if ((response && !addAnother) || !response) {
                setAddRolesVisible(false);
              }
              onRefresh();
            }}
          />
        )}
        {licenseAttrVisible && selectedAssignment && (
          <UpdateRoleAttributesDialog
            editLicenseManagerRoleAttributes={hasGlobalLicensemanagerPermission && selectedAssignment.role === licenseManagerRoleName}
            identityType={IdentityType.Group}
            scopeId={selectedAssignment.scopeId}
            identityId={selectedAssignment.identityId}
            onClose={(newExpirationDate?: Date) => {
              if (newExpirationDate && selectedAssignment.expirationDate !== newExpirationDate) {
                groupRolesStore
                  .updateExpiration(selectedAssignment.id, { scopeId: selectedAssignment.scopeId, newExpirationDate: newExpirationDate })
                  .then(() => {
                    groupRolesStore.load(query);
                  });
              }
              setLicenseAttrVisible(false);
            }}
            roleExpirationDate={selectedAssignment.expirationDate}
          />
        )}
      </Spin>
    </div>
  );
};

export default GroupLicenseRolesTab;
